import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedApi } from './api';
import LoadingSpinner from './components/LoadingSpinner';
import Comments from './components/Comments';
import { useOpportunities } from './hooks/useOpportunities';
import { formatTypeLabel } from './utils/formatTypeLabel';
import './Opportunities.css';

export default function Opportunities() {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedApi();
  
  const [allOpportunities, setAllOpportunities] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({ types: [], topics: [], rateMethods: [] });
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  
  // Filter states - load from localStorage if available
  const [searchTerm, setSearchTerm] = useState(() => {
    return localStorage.getItem('opportunities_searchTerm') || '';
  });
  const [selectedTypes, setSelectedTypes] = useState(() => {
    const saved = localStorage.getItem('opportunities_selectedTypes');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedCategories, setSelectedCategories] = useState(() => {
    const saved = localStorage.getItem('opportunities_selectedCategories');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedTopics, setSelectedTopics] = useState(() => {
    const saved = localStorage.getItem('opportunities_selectedTopics');
    return saved ? JSON.parse(saved) : [];
  });
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('opportunities_sortBy') || 'created';
  });

  // Category/Topic hierarchy
  const categoryTopics = {
    'Arts and Culture': ['Architecture', 'Art', 'Books', 'Comedy', 'Culture', 'Design', 'Entertainment', 'Film and TV', 'Food and Drink', 'Gaming', 'Home', 'Lifestyle', 'Literature', 'Music', 'Style', 'Travel'],
    'Content': ['Brand Writing', 'Copywriting', 'E-commerce', 'Ghostwriting', 'SEO'],
    'Creative': ['Essays', 'Memoirs', 'Non-Fiction', 'Novels', 'Poetry', 'Short Stories'],
    'News': ['Automotive', 'Business', 'Climate', 'Education', 'Finance', 'Health', 'Identity', 'Investigative', 'Justice', 'Labor', 'Law', 'Mental Health', 'News', 'Opinion', 'Politics', 'Public Policy', 'Science', 'Service Journalism', 'Sports', 'Technology', 'Wellness']
  };

  // Stable query key for React Query (avoid new object identity every render)
  const queryParams = useMemo(() => {
    const p = {
      sortBy,
      sortOrder: 'DESC',
      limit: 20,
      offset
    };
    if (searchTerm) p.search = searchTerm;
    if (selectedTypes.length > 0) p.types = selectedTypes.join(',');
    if (selectedCategories.length > 0) p.areas = selectedCategories.join(',');
    if (selectedTopics.length > 0) p.topics = selectedTopics.join(',');
    return p;
  }, [
    sortBy,
    offset,
    searchTerm,
    selectedTypes,
    selectedCategories,
    selectedTopics
  ]);
  
  // Use React Query for caching
  const { data, isLoading, error, refetch } = useOpportunities(queryParams);
  
  // Update opportunities when data changes
  useEffect(() => {
    if (data) {
      if (offset === 0) {
        // Initial load - replace all opportunities
        setAllOpportunities(data.data || []);
        setTotalCount(data.total || 0);
      } else {
        // Load more - append to existing opportunities
        setAllOpportunities(prevOpps => [...prevOpps, ...(data.data || [])]);
      }
      
      // Check if there are more opportunities to load
      setHasMore((data.data || []).length === 20);
    }
  }, [data, offset]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Define the order and labels for opportunity types
  const typeOrder = useMemo(() => ['Gigs', 'Full', 'Part', 'Temp', 'Creative', 'Editor', 'Fellowship', 'Internship', 'Grant', 'Unpaid'], []);

  const fetchFilters = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/opportunities/filters`);
      if (response.ok) {
        const data = await response.json();
        
        // Deduplicate types by their normalized values and filter out unwanted types
        const typeMap = new Map();
        const excludeTypes = ['deadline', 'evergreen'];
        data.types.forEach(type => {
          const normalized = type.toLowerCase();
          if (!typeMap.has(normalized) && !excludeTypes.includes(normalized)) {
            typeMap.set(normalized, type);
          }
        });

        // Sort types according to the defined order
        const uniqueTypes = Array.from(typeMap.values());
        const sortedTypes = uniqueTypes.sort((a, b) => {
          const indexA = typeOrder.findIndex(t => t.toLowerCase() === a.toLowerCase());
          const indexB = typeOrder.findIndex(t => t.toLowerCase() === b.toLowerCase());
          
          // If both are in the order list, sort by their position
          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          
          // If only one is in the order list, prioritize it
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          
          // If neither is in the order list, sort alphabetically
          return a.localeCompare(b);
        });

        setFilters({
          ...data,
          types: sortedTypes
        });
      }
    } catch (err) {
      console.error('Error fetching filters:', err);
    } finally {
      setFiltersLoaded(true);
    }
  }, [authenticatedFetch, typeOrder]);

  // Fetch filters when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      setFiltersLoaded(false);
      fetchFilters();
    }
  }, [isAuthenticated, fetchFilters]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('opportunities_searchTerm', searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    localStorage.setItem('opportunities_selectedTypes', JSON.stringify(selectedTypes));
  }, [selectedTypes]);

  useEffect(() => {
    localStorage.setItem('opportunities_selectedCategories', JSON.stringify(selectedCategories));
  }, [selectedCategories]);

  useEffect(() => {
    localStorage.setItem('opportunities_selectedTopics', JSON.stringify(selectedTopics));
  }, [selectedTopics]);

  useEffect(() => {
    localStorage.setItem('opportunities_sortBy', sortBy);
  }, [sortBy]);

  // Reset offset when filters change (but not when just loading more)
  useEffect(() => {
    if (isAuthenticated) {
      setOffset(0); // Reset offset when filters change
    }
  }, [isAuthenticated, searchTerm, selectedTypes, selectedCategories, selectedTopics, sortBy]);

  // React Query handles all data fetching automatically

  const formatRate = (opportunity) => {
    if (opportunity.rate) return opportunity.rate;
    if (opportunity.calculated_rate) return `$${opportunity.calculated_rate}`;
    if (opportunity.word_rate) return `$${opportunity.word_rate}/word`;
    if (opportunity.piece_rate) return `$${opportunity.piece_rate}/piece`;
    return 'Rate not specified';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const stripHtmlAndTruncate = (html, maxLength = 300) => {
    if (!html) return '';
    
    // Strip HTML tags
    const text = html.replace(/<[^>]*>/g, '');
    
    // Decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    const decoded = textarea.value;
    
    // Truncate to maxLength
    if (decoded.length <= maxLength) return decoded;
    
    // Find last complete word within limit
    const truncated = decoded.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    
    return lastSpace > 0 ? truncated.substring(0, lastSpace) + '...' : truncated + '...';
  };

  const loadMore = () => {
    setOffset(prevOffset => prevOffset + 20);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTypes([]);
    setSelectedCategories([]);
    setSelectedTopics([]);
    setSortBy('created');
    setOffset(0); // Reset to first page
  };

  const toggleType = (type) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        // Remove category and all its topics
        const topicsToRemove = categoryTopics[category] || [];
        setSelectedTopics(topics => topics.filter(t => !topicsToRemove.includes(t)));
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  const toggleTopic = (topic) => {
    setSelectedTopics(prev => 
      prev.includes(topic) 
        ? prev.filter(t => t !== topic)
        : [...prev, topic]
    );
  };

  const getCategoryColor = (category) => {
    const colorMap = {
      'Arts and Culture': '#3864B4',
      'Creative': '#b438a2',
      'Content': '#b48838',
      'News': '#38B44a'
    };
    return colorMap[category] || '#666';
  };

  const getOpportunityCategories = (opportunity) => {
    if (!opportunity.areas) return [];
    
    const areas = opportunity.areas.split(',').map(a => a.trim());
    const categories = [];
    
    // Map areas directly to categories (areas field contains category names)
    areas.forEach(area => {
      const normalized = area.toLowerCase();
      if (normalized.includes('arts') || normalized.includes('culture')) {
        if (!categories.includes('Arts and Culture')) categories.push('Arts and Culture');
      } else if (normalized === 'creative') {
        if (!categories.includes('Creative')) categories.push('Creative');
      } else if (normalized === 'content') {
        if (!categories.includes('Content')) categories.push('Content');
      } else if (normalized === 'news') {
        if (!categories.includes('News')) categories.push('News');
      }
    });
    
    return categories;
  };

  const getCategoryGradient = (categories) => {
    if (!categories || categories.length === 0) return '#e0e0e0';
    if (categories.length === 1) return getCategoryColor(categories[0]);
    
    // Create gradient with equal sections for each category
    const percentage = 100 / categories.length;
    const gradientStops = categories.map((cat, index) => {
      const start = index * percentage;
      const end = (index + 1) * percentage;
      return `${getCategoryColor(cat)} ${start}%, ${getCategoryColor(cat)} ${end}%`;
    }).join(', ');
    
    return `linear-gradient(to bottom, ${gradientStops})`;
  };

  if (authLoading) {
    return (
      <div style={{ padding: '4rem 2rem', textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
        <LoadingSpinner message="Authenticating..." />
      </div>
    );
  }

  // Show error state only if we've exhausted retries
  if (error && !isLoading) {
    return (
      <div style={{ padding: '4rem 2rem', textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h2 style={{ color: '#dc3545', marginBottom: '1rem' }}>Unable to load opportunities</h2>
          <p style={{ color: '#666', marginBottom: '1.5rem' }}>
            There was a problem connecting to the server. Please try again.
          </p>
          <button
            onClick={() => refetch()}
            style={{
              backgroundColor: '#38b44a',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      
      <div className="opportunities-container" style={{ padding: '2rem', paddingTop: 'calc(2rem + 20px)', width: '100%', maxWidth: '100%', margin: '0 auto', boxSizing: 'border-box', overflow: 'visible', position: 'relative' }}>

      {/* Mobile overlay */}
      <div 
        className={`mobile-overlay ${mobileFiltersOpen ? 'open' : ''}`}
        onClick={() => setMobileFiltersOpen(false)}
      ></div>

      {/* Mobile filter toggle button */}
      <button 
        className="mobile-filter-button"
        onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
        aria-label="Toggle filters"
      >
        Show Filters
      </button>

      {/* Main Layout - Sidebar + Content */}
      <div className="opportunities-main-layout" style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', width: '100%' }}>
        {/* Left Sidebar - Filters */}
        <div className={`opportunities-filter-sidebar ${mobileFiltersOpen ? 'open' : ''}`}>
          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            paddingTop: '1.5rem',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'relative'
          }}>
            {/* Mobile Close Button */}
            <button
              onClick={() => setMobileFiltersOpen(false)}
              style={{
                display: 'none',
                width: '100%',
                backgroundColor: 'white',
                border: '2px solid #d32f2f',
                borderRadius: '6px',
                fontSize: '16px',
                color: '#d32f2f',
                cursor: 'pointer',
                padding: '0.75rem',
                fontWeight: '600',
                marginBottom: '1rem',
                transition: 'all 0.2s'
              }}
              className="mobile-close-button"
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#ffebee';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'white';
              }}
            >
              Hide Filters
            </button>

            {/* Clear Filters Button / Header */}
            {/* Opportunity Count */}
            {!isLoading && (
              <div style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                border: '1px solid #e9ecef'
              }}>
                <p style={{ 
                  fontSize: '0.875rem',
                  color: '#666',
                  margin: 0,
                  textAlign: 'center',
                  fontWeight: '500'
                }}>
                  {totalCount} opportunities available
                </p>
              </div>
            )}

            <button
              onClick={clearFilters}
              disabled={!searchTerm && selectedTypes.length === 0 && selectedCategories.length === 0 && selectedTopics.length === 0}
              style={{
                width: '100%',
                backgroundColor: '#f8f8f8',
                color: '#666',
                border: '2px solid #ddd',
                padding: '10px',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: (searchTerm || selectedTypes.length > 0 || selectedCategories.length > 0 || selectedTopics.length > 0) ? 'pointer' : 'default',
                fontWeight: '500',
                transition: 'all 0.2s',
                marginBottom: '1rem'
              }}
              onMouseOver={(e) => {
                if (!e.target.disabled) {
                  e.target.style.backgroundColor = '#efefef';
                  e.target.style.borderColor = '#999';
                }
              }}
              onMouseOut={(e) => {
                if (!e.target.disabled) {
                  e.target.style.backgroundColor = '#f8f8f8';
                  e.target.style.borderColor = '#ddd';
                }
              }}
            >
              {searchTerm || selectedTypes.length > 0 || selectedCategories.length > 0 || selectedTopics.length > 0 
                ? 'Clear All Filters' 
                : 'Filter Opportunities'}
            </button>

            {/* Sort By */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '14px', color: '#333' }}>
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                }}
              >
                <option value="created">Date Posted</option>
                <option value="calculated_rate">Highest Paid</option>
              </select>
            </div>

            {/* Search */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '14px', color: '#333' }}>
                Search
              </label>
              <input
                type="text"
                placeholder="publication name or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Categories and Topics (Areas) */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '14px', color: '#333' }}>
                Areas {(selectedCategories.length > 0 || selectedTopics.length > 0) && 
                  <span style={{ color: '#38b44a' }}>({selectedCategories.length + selectedTopics.length})</span>}
              </div>
              
              {Object.keys(categoryTopics).map(category => (
                <div key={category} style={{ marginBottom: '4px' }}>
                  {/* Category Checkbox with Pill */}
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    marginBottom: '3px', 
                    cursor: 'pointer', 
                    fontSize: '14px',
                    fontWeight: '600',
                    padding: '2px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category)}
                      onChange={() => toggleCategory(category)}
                      style={{ marginRight: '8px', cursor: 'pointer' }}
                    />
                    <span style={{
                      backgroundColor: getCategoryColor(category),
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}>
                      {category === 'Arts and Culture' ? 'Arts & Culture' : category}
                    </span>
                  </label>
                  
                  {/* Subcategories - Only show when category is checked */}
                  {selectedCategories.includes(category) && (
                    <div style={{ marginLeft: '24px', paddingLeft: '12px', borderLeft: '2px solid #e0e0e0' }}>
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {categoryTopics[category].map(topic => (
                          <label key={topic} style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            marginBottom: '1px', 
                            cursor: 'pointer', 
                            fontSize: '13px',
                            padding: '2px 4px',
                            borderRadius: '4px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <input
                              type="checkbox"
                              checked={selectedTopics.includes(topic)}
                              onChange={() => toggleTopic(topic)}
                              style={{ marginRight: '8px', cursor: 'pointer' }}
                            />
                            <span>{topic}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Opportunity Type - Checkboxes */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '14px', color: '#333' }}>
                Opportunity Type {selectedTypes.length > 0 && <span style={{ color: '#38b44a' }}>({selectedTypes.length})</span>}
              </div>
              <div>
                {filters.types.map(type => (
                  <label key={type} style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    marginBottom: '4px', 
                    cursor: 'pointer', 
                    fontSize: '14px',
                    padding: '2px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(type)}
                      onChange={() => toggleType(type)}
                      style={{ marginRight: '8px', cursor: 'pointer' }}
                    />
                    <span>{formatTypeLabel(type)}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Content - Opportunities List */}
        <div style={{ 
          flex: 1, 
          minWidth: 0,
          width: 'calc(100% - 280px - 2rem)'
        }}>
            
            {/* Loading Overlay */}
            {isLoading && (
              <div style={{
                position: 'sticky',
                top: '0',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                padding: '2rem',
                textAlign: 'center',
                zIndex: 5,
                marginBottom: '1rem'
              }}>
                <div style={{
                  display: 'inline-flex',
                  backgroundColor: 'white',
                  padding: '20px 40px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '3px solid #e0e0e0',
                    borderTop: '3px solid #38b44a',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span style={{ fontSize: '16px', color: '#333', fontWeight: '500' }}>
                    Loading opportunities...
                  </span>
                </div>
              </div>
            )}

      {/* Opportunities List */}
      {!isLoading && allOpportunities.length === 0 && !error ? (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '3rem 2rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#333', marginBottom: '1rem' }}>
            No Opportunities Found
          </h2>
          <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '1rem' }}>
            Try adjusting your filters or search terms.
          </p>
          <button
            onClick={clearFilters}
            style={{
              backgroundColor: '#38b44a',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Clear All Filters
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1.5rem', width: '100%', maxWidth: '100%' }}>
          {allOpportunities.map((opportunity) => (
            <div
              key={opportunity.id}
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                transition: 'box-shadow 0.2s',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
                display: 'flex',
                position: 'relative'
              }}
              onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
              onMouseOut={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'}
            >
              {/* Colored vertical line */}
              <div style={{
                width: '10px',
                background: getCategoryGradient(getOpportunityCategories(opportunity)),
                borderTopLeftRadius: '8px',
                borderBottomLeftRadius: '8px',
                flexShrink: 0
              }}></div>
              
              {/* Card content */}
              <div className="opportunity-card-content" style={{ flex: 1, padding: '1.5rem', minWidth: 0 }}>
              {/* Top Row - Company/Pay and Date */}
              <div className="opportunity-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', width: '100%', gap: '1rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: '1.125rem', color: '#333', fontWeight: '600', margin: '0 0 0.25rem 0', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    {opportunity.brand_name}
                  </p>
                  <div style={{ fontSize: '1rem', fontWeight: '600', color: '#38b44a' }}>
                    {formatRate(opportunity)}
                  </div>
                </div>
                <div className="opportunity-date" style={{ fontSize: '0.875rem', color: '#666', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  Posted {formatDate(opportunity.created)}
                </div>
              </div>

              {/* Title */}
              <div style={{ marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#333', margin: '0', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                  {opportunity.title}
                </h3>
              </div>

              {/* Badges - Types */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                {opportunity.type && opportunity.type.split(',').map((type, index) => (
                  <span key={index} style={{
                    backgroundColor: '#f0f0f0',
                    color: '#666',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}>
                    {formatTypeLabel(type.trim())}
                  </span>
                ))}
              </div>

              {opportunity.details && (
                <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '1rem', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                  {stripHtmlAndTruncate(opportunity.details, 300)}
                </p>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', flex: 1 }}>
                  {opportunity.topic && opportunity.topic.split(',').map((topic, index) => (
                    <span key={index} style={{
                      backgroundColor: 'white',
                      color: '#333',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      border: '1px solid #ddd'
                    }}>
                      {topic.trim()}
                    </span>
                  ))}
                </div>
                <button 
                  onClick={() => navigate(`/opportunity/${opportunity.hash}`)}
                  style={{
                    backgroundColor: '#38b44a',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#2d8f3a'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#38b44a'}
                >
                  View Details
                </button>
              </div>
              
              {/* Comments Section */}
              <div className="opportunity-comments-section" style={{ padding: '0 1.5rem 1.5rem 1.5rem' }}>
                <Comments 
                  contentId={opportunity.id} 
                  contentType="opportunity"
                  countOnly={true}
          deferUntilReady={!isLoading && filtersLoaded}
                  onAddCommentClick={() => {
                    const state = { showComments: true, showForm: true };
                    navigate(`/opportunity/${opportunity.hash}`, { state });
                  }}
                  onShowCommentsClick={() => {
                    const state = { showComments: true, showForm: false };
                    navigate(`/opportunity/${opportunity.hash}`, { state });
                  }}
                />
              </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Load More Button */}
      {!isLoading && allOpportunities.length > 0 && hasMore && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '3rem',
          marginBottom: '2rem'
        }}>
          <button
            onClick={loadMore}
            disabled={isLoading}
            style={{
              backgroundColor: isLoading ? '#ccc' : '#38b44a',
              color: 'white',
              border: 'none',
              padding: '14px 32px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseOver={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#2d8f3a';
              }
            }}
            onMouseOut={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#38b44a';
              }
            }}
          >
            {isLoading && <LoadingSpinner size="small" message="" />}
            {isLoading ? 'Loading...' : 'Load More Opportunities'}
          </button>
        </div>
      )}
        </div>
      </div>
    </div>
    </>
  );
}
